import mapboxgl from 'mapbox-gl'
import { LAYER_STYLES, PIN_MARKER_SVG, GEOMETRY_ICONS, POPUP_CONFIG, generateLayerColor } from './layer_config'

export class LayerManager {
  constructor(map) {
    this.map = map
    this.layers = []
    this.markerLoadPromise = null
    this.currentPopup = null
    this.lastClickedFeatureId = null
    this.setupGlobalInteractivity()
  }

  // ============================================================================
  // MARKER LOADING
  // ============================================================================

  async loadPinMarker() {
    const markerId = 'pin-marker'
    if (this.map.hasImage(markerId)) return markerId
    if (this.markerLoadPromise) return await this.markerLoadPromise

    this.markerLoadPromise = (async () => {
      const img = new Image()
      img.src = `data:image/svg+xml,${encodeURIComponent(PIN_MARKER_SVG)}`
      await new Promise((resolve, reject) => {
        img.onload = resolve
        img.onerror = reject
      })
      this.map.addImage(markerId, img, { sdf: true })
      return markerId
    })()

    return await this.markerLoadPromise
  }

  // ============================================================================
  // GEOJSON LOADING & LAYER CREATION
  // ============================================================================

  async loadGeoJSON(url, index) {
    const response = await fetch(encodeURI(url))
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status} for ${url}`)

    const geojson = await response.json()
    const name = url.split('/').pop()
    return await this.loadGeoJSONData(geojson, name, index)
  }

  async loadGeoJSONData(geojson, filename, index) {
    if (!geojson || typeof geojson !== 'object') {
      throw new Error(`Invalid GeoJSON data for ${filename}`)
    }

    const displayName = (filename || '').toString().replace(/\.geojson$/i, '')
    const slug = displayName
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')

    const sourceId = `source-${index}-${slug}`
    const layerId = `layer-${index}-${slug}`

    const existingLayer = this.layers.find(l => l.id === layerId)
    if (existingLayer) return { sourceId, layerId, geojson, layer: existingLayer }

    const geometryType = geojson.features?.[0]?.geometry?.type
    const layerColor = generateLayerColor(index, geometryType)

    const layerMetadata = {
      id: layerId,
      name: displayName,
      visible: true,
      type: geometryType,
      color: layerColor
    }
    this.layers.push(layerMetadata)

    this.map.addSource(sourceId, { type: 'geojson', data: geojson })

    const isPoint = geometryType === 'Point' || geometryType === 'MultiPoint'
    if (isPoint) await this.loadPinMarker()

    this.addLayerByType(layerId, sourceId, geometryType, layerColor, isPoint ? 'pin-marker' : null)

    return { sourceId, layerId, geojson, layer: layerMetadata }
  }

  // ============================================================================
  // LAYER RENDERING
  // ============================================================================

  addLayerByType(layerId, sourceId, geometryType, layerColor, markerId = null) {
    const isPoint = geometryType === 'Point' || geometryType === 'MultiPoint'
    const isLine = geometryType === 'LineString' || geometryType === 'MultiLineString'
    const isPolygon = geometryType === 'Polygon' || geometryType === 'MultiPolygon'

    if (isPoint) this.addPointLayer(layerId, sourceId, layerColor, markerId)
    else if (isLine) this.addLineLayer(layerId, sourceId, layerColor)
    else if (isPolygon) this.addPolygonLayer(layerId, sourceId, layerColor)
  }

  addPointLayer(layerId, sourceId, color, markerId) {
    this.map.addLayer({
      id: layerId,
      source: sourceId,
      ...LAYER_STYLES.point,
      layout: {
        ...LAYER_STYLES.point.layout,
        'icon-image': markerId
      },
      paint: {
        ...LAYER_STYLES.point.paint,
        'icon-color': color
      }
    })
  }

  addLineLayer(layerId, sourceId, color) {
    this.map.addLayer({
      id: layerId,
      source: sourceId,
      ...LAYER_STYLES.line,
      paint: {
        ...LAYER_STYLES.line.paint,
        'line-color': color
      }
    })
  }

  addPolygonLayer(layerId, sourceId, color) {
    this.map.addLayer({
      id: layerId,
      source: sourceId,
      ...LAYER_STYLES.polygon.fill,
      paint: { ...LAYER_STYLES.polygon.fill.paint, 'fill-color': color }
    })

    this.map.addLayer({
      id: `${layerId}-outline`,
      source: sourceId,
      ...LAYER_STYLES.polygon.outline,
      paint: { ...LAYER_STYLES.polygon.outline.paint, 'line-color': color }
    })
  }

  // ============================================================================
  // MAP INTERACTIVITY
  // ============================================================================

  setupGlobalInteractivity() {
    this.map.on('click', (e) => {
      if (!this.map.isStyleLoaded()) return

      const features = this.map.queryRenderedFeatures(e.point)
      const feature = features.find(f => this.layers.some(l => l.id === f.layer.id))

      if (feature) {
        const featureId = `${feature.layer.id}-${feature.id || JSON.stringify(feature.geometry.coordinates)}`

        if (this.currentPopup && this.lastClickedFeatureId === featureId) {
          this.closePopup()
          return
        }

        this.closePopup()
        this.showPopup(e.lngLat, feature)
        this.lastClickedFeatureId = featureId
      } else {
        this.closePopup()
      }
    })

    this.map.on('mousemove', (e) => {
      if (!this.map.isStyleLoaded()) return
      const features = this.map.queryRenderedFeatures(e.point)
      const hasFeature = features.some(f => this.layers.some(l => l.id === f.layer.id))
      this.map.getCanvas().style.cursor = hasFeature ? 'pointer' : ''
    })
  }

  // ============================================================================
  // POPUP MANAGEMENT
  // ============================================================================

  showPopup(lngLat, feature) {
    const layer = this.layers.find(l => l.id === feature.layer.id)
    const contentEl = this.buildPopupElement(layer, feature.properties)

    this.currentPopup = new mapboxgl.Popup(POPUP_CONFIG)
      .setLngLat(lngLat)
      .setDOMContent(contentEl)
      .addTo(this.map)

    // Drive header/tip styling from CSS var (no <style> injection)
    this.currentPopup.getElement().style.setProperty('--popup-accent', layer.color)

    this.currentPopup.on('close', () => {
      this.currentPopup = null
      this.lastClickedFeatureId = null
    })
  }

  closePopup() {
    if (this.currentPopup) {
      this.currentPopup.remove()
      this.currentPopup = null
      this.lastClickedFeatureId = null
    }
  }

  buildPopupElement(layer, properties) {
    const popupTemplate = document.getElementById('mapbox-popup-template')
    const popupRowTemplate = document.getElementById('mapbox-popup-row-template')

    // Minimal fallback if templates are missing for any reason
    if (!popupTemplate?.content?.firstElementChild) {
      const fallback = document.createElement('div')
      fallback.textContent = layer?.name || ''
      return fallback
    }

    const popupEl = popupTemplate.content.firstElementChild.cloneNode(true)

    const titleEl = popupEl.querySelector('[data-popup-title]')
    const iconEl = popupEl.querySelector('[data-popup-icon]')
    const tableEl = popupEl.querySelector('[data-popup-table]')
    const emptyEl = popupEl.querySelector('[data-popup-empty]')

    if (titleEl) titleEl.textContent = layer.name
    if (iconEl) iconEl.innerHTML = this.getGeometryIcon(layer.type, layer.color)

    const entries = Object.entries(properties ?? {}).filter(([, v]) => v !== null && v !== undefined && v !== '')

    if (!tableEl || entries.length === 0) {
      if (emptyEl) emptyEl.classList.remove('hidden')
      return popupEl
    }

    tableEl.classList.remove('hidden')

    const hasRowTemplate = !!popupRowTemplate?.content?.firstElementChild
    const createRow = hasRowTemplate
      ? () => {
        const rowEl = popupRowTemplate.content.firstElementChild.cloneNode(true)
        return {
          rowEl,
          keyEl: rowEl.querySelector('[data-popup-key]'),
          valueEl: rowEl.querySelector('[data-popup-value]')
        }
      }
      : () => {
        const rowEl = document.createElement('tr')
        const keyEl = document.createElement('td')
        const valueEl = document.createElement('td')
        rowEl.appendChild(keyEl)
        rowEl.appendChild(valueEl)
        return { rowEl, keyEl, valueEl }
      }

    entries.forEach(([key, value]) => {
      const { rowEl, keyEl, valueEl } = createRow()
      if (keyEl) keyEl.textContent = `${this.formatKey(key)}:`
      if (valueEl) valueEl.textContent = String(value)
      tableEl.appendChild(rowEl)
    })

    return popupEl
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  getGeometryIcon(type, color) {
    if (type?.includes('Point')) return GEOMETRY_ICONS.point(color)
    if (type?.includes('Line')) return GEOMETRY_ICONS.line(color)
    if (type?.includes('Polygon')) return GEOMETRY_ICONS.polygon(color)
    return GEOMETRY_ICONS.point(color)
  }

  formatKey(key) {
    return key
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  }

  toggleLayer(layerId, visible) {
    const visibility = visible ? 'visible' : 'none'
    this.map.setLayoutProperty(layerId, 'visibility', visibility)
    if (this.map.getLayer(`${layerId}-outline`)) {
      this.map.setLayoutProperty(`${layerId}-outline`, 'visibility', visibility)
    }
  }

  getAllLayers() {
    return this.layers
  }
}
